/* eslint-disable camelcase */
import React, { useEffect, useMemo } from 'react'

import transmitigator, { TransmitigatorOptions } from '@ally/transmitigator'
import { HostServices } from '@ally/federated-types'

import { SessionContext } from './SessionContext'
import { TransmitContext } from './TransmitContext'
import { AppStateProvider } from './AppStateProvider'
import { FlagContext } from './FlagContext'

export interface AllyLoginProviderProps extends TransmitigatorOptions {
  rehydrates?: boolean
  isInPortal?: boolean
  redirects?: boolean
  cancelButton?: React.ReactNode
  journey_version?: string
  hostServices?: HostServices
}

function isApplicationEntry(): boolean {
  return /^\/(forgot|reset)-password/.test(window.location.pathname)
}

/**
 * A Provider for the AllyLogin SessionContext.
 * This MUST wrap either the AllyLoginPortal or AllyLoginRouter components.
 *
 * NOTE: This actually provides 2 contexts: SessionContext and TransmitContext.
 * The latter is for internal use only and isn't exported by this component.
 * They contain roughly the same data, except that SessionContext omits the
 * `activeScreen` value and is a bit more user friendly from a consumer point
 * of view.
 *
 * @param props.rehydrate - If true (default), this will attempt a session
 * rehydration before displaying the LoginForm. If the user is authenticated,
 * then no login form will be displayed.
 */
export const AllyLoginProvider: React.FC<AllyLoginProviderProps> = ({
  children,
  rehydrates = true,
  isInPortal = false,
  redirects = true,
  cancelButton = null,
  journey_version = 'v2',
  hostServices,
  ...options
}) => {
  const transmitRef = hostServices?.transmitRef
  const flagContextValue = hostServices?.featureFlags
  const logger = hostServices?.log

  const { useTransmit } = useMemo(() => {
    if (transmitRef) {
      return transmitRef
    }
    // TODO: we shouldn't be excluding options from the dep array, we're just getting a new object each render due to object spreading
    return transmitigator(options)
  }, [transmitRef])

  const transmit = useTransmit()
  const { actions, activeScreen, sessionStatus } = transmit

  useEffect(() => {
    if (rehydrates && !isApplicationEntry()) {
      transmit.actions.rehydrate({ journey_version: 'v2' })
    }
  }, [])

  const isAuthenticated = activeScreen.type === 'Authenticated'

  return (
    <TransmitContext.Provider
      value={{
        ...transmit,
        isInPortal,
        redirects,
        cancelButton,
        journey_version: 'v2',
        hostServices,
        logger,
      }}
    >
      <FlagContext.Provider value={flagContextValue as any}>
        <SessionContext.Provider
          value={{ ...actions, sessionStatus, isAuthenticated }}
        >
          <AppStateProvider>{children}</AppStateProvider>
        </SessionContext.Provider>
      </FlagContext.Provider>
    </TransmitContext.Provider>
  )
}
